/**
 * KIT product presentation helpers — isolated so future changes
 * (approved Amazon product-content provider, curated display-name
 * overrides authored by the Hair & Makeup Supervisor) can be
 * plugged in here without restructuring the catalogue UI.
 *
 * Iter-158.13.2.20f.16 — introduced for the visual-card upgrade.
 *
 * IMPORTANT — non-negotiable rules mirrored in the schema README:
 *   • display_name is PRESENTATION ONLY. It never alters ASIN
 *     identity or the outbound Amazon destination. The canonical
 *     Amazon `name` remains preserved on the product record and
 *     is the only value used for `alt` text fallback, sorting,
 *     analytics identity or catalogue reconstitution.
 *   • Product imagery MUST NOT be scraped, extracted or hotlinked
 *     from Amazon. `product.image` is populated ONLY when we hold
 *     a compliant Amazon-approved product-content asset. Until
 *     then the neutral fallback is rendered — the caller is
 *     responsible for matching card geometry across both states.
 */

/**
 * Resolve the display name for a product row.
 *
 * Order of preference:
 *   1. `product.display_name` — a supervisor-reviewed, mobile-friendly
 *      alternative to the SEO-heavy Amazon title. Optional. When
 *      present it is used verbatim.
 *   2. `product.name` — the canonical Amazon listing title. Always
 *      present in the manifest. Fallback when no reviewed display
 *      name exists yet.
 *
 * Never invents copy. Never falls back to ASIN.
 */
export function resolveKitProductDisplayName(product) {
    if (!product) return "";
    const displayName = typeof product.display_name === "string"
        ? product.display_name.trim()
        : "";
    if (displayName) return displayName;
    return (product.name || "").trim();
}

/**
 * Resolve the product image descriptor for a card slot.
 *
 * Returns `{ src, alt }` when a compliant image is available on the
 * product record, otherwise `null` so the card renders the neutral
 * fallback. This helper is the extension point for a future
 * approved Amazon product-content provider: swapping the body of
 * this function for a lookup by ASIN against an approved image
 * catalogue is all the FE needs to gain real product imagery.
 * The catalogue UI never talks to Amazon directly.
 */
export function resolveKitProductImage(product) {
    if (!product) return null;
    const img = product.image;
    if (!img || typeof img.src !== "string" || !img.src.trim()) return null;
    return {
        src: img.src,
        alt: (typeof img.alt === "string" && img.alt.trim())
            || product.name
            || "",
    };
}
